'use client';

import './chip.scss';

import { useTranslation } from 'react-i18next';

import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.js';
import { IconNames } from '@sola/schema/lib/icons/icon-names.js';

import { cn } from '../../utils/helpers';

export type Chip = {
  name: string;
  value: string;
  icon?: IconNames;
  isClear?: boolean;
  isSelected?: boolean;
  onClick?: () => void;
  className?: string;
  onRemove?: () => void;
  removable?: boolean;
  size?: 'sm' | 'md';
};

const sizeClasses: Record<'sm' | 'md', string> = {
  sm: 'gap-snug-md px-snug-xl py-snug-md',
  md: 'gap-snug-md px-comfy-xs py-snug-xl',
};

export const Chip: React.FC<Chip> = ({
  name,
  value,
  icon,
  isSelected,
  onClick,
  className,
  onRemove,
  removable,
  size = 'md',
}) => {
  const { t } = useTranslation();

  return (
    <button
      type="button"
      aria-pressed={isSelected}
      onClick={onClick}
      key={value}
      className={cn(
        `Chip flex items-center justify-center label label-moderate ${sizeClasses[size]}`,
        className,
        {
          'Chip--active': isSelected,
        },
      )}
    >
      {icon && <ArkIcon iconName={icon} />}
      {name}

      {removable && (
        <span
          role="button"
          tabIndex={0}
          className="Chip__close ml-snug-xs flex items-center justify-center p-none"
          onClick={(e) => {
            e.stopPropagation();
            onRemove?.();
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              e.stopPropagation();
              onRemove?.();
            }
          }}
          aria-label={t('giftCatalog.remove', { name })}
        >
          <ArkIcon iconName="x-close" />
        </span>
      )}
    </button>
  );
};
